import type { Skill } from '@/core/types';
import { catalog } from '@/data/catalog';
import { clamp01, fold } from '@/core/util/misc';
import { L } from '@/i18n';

/**
 * FEYNMAN ANALYSIS — local, transparent heuristics (not AI).
 * The student's explanation is compared with the skill's key ideas,
 * misconception descriptions and prerequisite vocabulary.
 */

export interface FeynmanResult {
  scores: { correctness: number; completeness: number; clarity: number; simplicity: number };
  overall: number;
  covered: string[];
  missing: string[];
  misconceptions: { wrong: string; fix: string }[];
  missingPrerequisites: string[];
  clarityNotes: string[];
  nextStep: string;
  /** Warm overall feedback (AI analysis only). */
  summary?: string;
  source?: 'ai' | 'local';
}

const JARGON_HINT = /\b(thus|hence|therefore|whereby|notwithstanding|aforementioned|følgelig|herved)\b/gi;

function sentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
}

function hasAny(t: string, kws: string[]) {
  return kws.some((k) => t.includes(fold(k)));
}

/**
 * The key idea's own words count too (in the active language), so an
 * explanation written in Norwegian is matched against the Norwegian idea.
 * At least half of its longer words must appear (by their first five letters).
 */
function ideaHit(t: string, idea: string) {
  const words = fold(idea).split(/[^a-z0-9æøå]+/).filter((w) => w.length >= 5);
  if (!words.length) return false;
  const hits = words.filter((w) => t.includes(w.slice(0, 5))).length;
  return hits >= Math.ceil(words.length / 2);
}

const covers = (t: string, k: { idea: string; keywords: string[] }) => hasAny(t, k.keywords) || ideaHit(t, k.idea);

export function analyseExplanation(skill: Skill, text: string): FeynmanResult {
  const t = ` ${fold(text)} `;
  const words = text.trim().split(/\s+/).filter(Boolean);
  const keys = skill.content.keyIdeas.length ? skill.content.keyIdeas : skill.content.definitions.map((d) => ({ idea: d.term, keywords: [d.term] }));
  const covered = keys.filter((k) => covers(t, k)).map((k) => k.idea);
  const missing = keys.filter((k) => !covers(t, k)).map((k) => k.idea);
  const completeness = keys.length ? covered.length / keys.length : 0.5;

  // misconception signals: phrases from the "wrong" description appearing as claims
  const misconceptions = skill.content.misconceptions
    .filter((m) => {
      const cue = fold(m.wrong).match(/\(([^)]+)\)/)?.[1];
      return cue ? t.includes(fold(cue).slice(0, 18)) : false;
    })
    .map((m) => ({ wrong: m.wrong, fix: m.fix }));
  const correctness = clamp01(0.55 + 0.45 * completeness - 0.35 * misconceptions.length);

  // prerequisites the explanation leans on but never names
  const prereqs = catalog.prerequisites(skill.id).map((id) => catalog.skill(id)!);
  const missingPrerequisites = prereqs
    .filter((p) => !hasAny(t, [p.name, ...p.aliases.slice(0, 3)]) && completeness < 0.75)
    .slice(0, 2)
    .map((p) => p.name);

  const sents = sentences(text);
  const avgLen = sents.length ? words.length / sents.length : words.length;
  const longWords = words.filter((w) => w.replace(/[^a-zA-ZæøåÆØÅ]/g, '').length >= 13).length;
  const jargon = (text.match(JARGON_HINT) ?? []).length;
  const clarityNotes: string[] = [];
  const noExample = !/(for example|e\.g\.|for eksempel|f\.eks|such as|like when|tenk deg|imagine|som når|akkurat som)/i.test(text);
  if (words.length < 25) clarityNotes.push(L('Very short — a good explanation usually needs an example.', 'Veldig kort — en god forklaring trenger som regel et eksempel.'));
  if (avgLen > 28) clarityNotes.push(L(`Average sentence length is ${Math.round(avgLen)} words — split long sentences.`, `Setningene er i snitt ${Math.round(avgLen)} ord — del opp de lange.`));
  if (noExample) clarityNotes.push(L('Add a concrete example — it is the fastest test of understanding.', 'Legg til et konkret eksempel — det er den raskeste testen på forståelse.'));
  if (longWords > 3 || jargon > 1) clarityNotes.push(L('Several long or technical words — explain them as if to a younger student.', 'Flere lange eller faglige ord — forklar dem som for en yngre elev.'));
  const clarity = clamp01(1 - (avgLen > 28 ? 0.25 : 0) - (words.length < 25 ? 0.35 : 0) - (noExample ? 0.2 : 0));
  const simplicity = clamp01(1 - 0.08 * longWords - 0.12 * jargon - (avgLen > 32 ? 0.2 : 0));
  const overall = clamp01(0.4 * correctness + 0.3 * completeness + 0.15 * clarity + 0.15 * simplicity);
  const nextStep = misconceptions.length
    ? L(`Fix the misconception first: ${misconceptions[0].fix}`, `Rett misforståelsen først: ${misconceptions[0].fix}`)
    : missing.length
      ? L(`Add the missing idea: "${missing[0]}" — then retry.`, `Ta med ideen som mangler: «${missing[0]}» — og prøv igjen.`)
      : clarityNotes.length
        ? clarityNotes[0]
        : L('Strong explanation. Try explaining a harder example next.', 'Sterk forklaring. Prøv å forklare et vanskeligere eksempel neste gang.');
  return { scores: { correctness, completeness, clarity, simplicity }, overall, covered, missing, misconceptions, missingPrerequisites, clarityNotes, nextStep };
}
